import { capitalize, type ComputedRef, type InjectionKey, type Ref } from "vue";
import moment from "moment";
import type { Moment } from "moment";
import {
  MetricPeriodType,
  type IncomeAccountSpecificationsQuery,
  type RawBalanceAccountSpecificationsQuery,
  type IncomeStatementAccountSpecificationsPortfolioQuery,
  type RawBalanceAccountSpecificationsPortfolioQuery,
  type BalancePropertyQuery,
  type BalancePortfolioQuery,
  type ResultsPropertyQuery,
  type ResultsPortfolioQuery,
} from "~/graphql/generated/graphql";
import { type TranslationKey, filterUniqueOn, flattenTree, getSet, groupBy, pushGet, sum } from "~/helpers/common-helpers";
import type { NamespacePortfolioPropertyBalance, DateRangeConfig } from "~/helpers/user-preferences-helpers";
import type { Currency } from "~/helpers/apollo/types";
import { hasValue } from "~/helpers/common-helpers";
import type { BudgetRecord, BudgetRecordsEntity, Category } from "~/helpers/entities/property-helpers";
import { getPeriodBudgetsIntersect, type AccountBudgetCell, type BudgetCell, type CategoryBudgetCell } from "~/helpers/budgets-helpers";
import type { Account } from "~/helpers/entities/account-helpers";
import type { BenchmarkGroupOption } from "~/helpers/modules/opex-helpers";

export type FinancialStatement = {
  id: string;
  explanation?: Nullable<string>;
  entryDate?: Nullable<string>;
  actualCostWithVat?: Nullable<number>;
  currency: string;
};

export const fitDate = (_date: Moment, periodType: MetricPeriodType) => {
  const date = moment.utc(_date);
  switch (periodType) {
    case "MONTHLY":
      return date.startOf("month");
    case "QUARTERLY":
      return date.startOf("quarter");
    case "YEARLY":
      return date.startOf("year");
    default:
      return date;
  }
};

export const fitDateString = (dateStr: string, periodType: MetricPeriodType) => fitDate(moment.utc(dateStr), periodType);

export const getPeriodHeader = (period: Moment, periodType: MetricPeriodType | null) => {
  switch (periodType) {
    case "MONTHLY":
      return capitalize(period.format("MMM YYYY"));
    case "QUARTERLY":
      return `Q${period.quarter()} ${period.year()}`;
    case "YEARLY":
      return capitalize(period.format("YYYY"));
    default:
      return " - ";
  }
};

export type FinanceCell = {
  period: string;
  isBlueprint: boolean;
  order: number;
};

export type CategoryCell = Omit<
  NonNullable<BalancePropertyQuery & BalancePortfolioQuery & ResultsPropertyQuery & ResultsPortfolioQuery>["categoryCells"][number],
  "__typename"
>;

export const isEqual = (next: { cellKey: string; value: number }, prev?: { cellKey: string; value: number }) =>
  !!prev && next.cellKey === prev.cellKey && next.value === prev.value;

export interface ColumnHeaderConfigBase {
  type: ColumnHeaderConfigType;
  label: string;
  colSpan: number;
  even: boolean;
  key: string;
}

export interface ValueHeaderConfigBase extends ColumnHeaderConfigBase {
  periodHeader: PeriodColumnHeaderConfig;
  value: ColumnHeaderConfigValue;
  type: ValueColumnHeaderType;
}

export type ColumnHeaderConfigType = ColumnHeaderConfig["type"];
export type ColumnHeaderConfigValue = ValueColumnHeaderConfig["value"];

export interface PeriodColumnHeaderConfig extends ColumnHeaderConfigBase {
  type: "period";
  period: Moment;
}

export interface TotalColumnHeaderConfig extends ValueHeaderConfigBase {
  type: "total";
  value: (row: AccountsRow) => number;
}

export interface ChangeColumnHeaderConfig extends ValueHeaderConfigBase {
  type: "change";
  value: (row: AccountsRow) => number;
}

export interface ChangePercentageColumnHeaderConfig extends ValueHeaderConfigBase {
  type: "changePercentage";
  value: (row: AccountsRow) => number;
}

export interface BudgetColumnHeaderConfig extends ValueHeaderConfigBase {
  type: "budget";
  value: (row: AccountsRow) => number;
}

export interface BudgetDiffColumnHeaderConfig extends ValueHeaderConfigBase {
  type: "budgetDiff";
  value: (row: AccountsRow) => number;
}

export interface BudgetForecastColumnHeaderConfig extends ValueHeaderConfigBase {
  type: "budgetForecast";
  value: (row: AccountsRow) => number;
}

export type ValueColumnHeaderType = ValueColumnHeaderConfig["type"];

export type ValueColumnHeaderConfig =
  | TotalColumnHeaderConfig
  | ChangePercentageColumnHeaderConfig
  | ChangeColumnHeaderConfig
  | BudgetColumnHeaderConfig
  | BudgetDiffColumnHeaderConfig
  | BudgetForecastColumnHeaderConfig;

export type ColumnHeaderConfig = PeriodColumnHeaderConfig | ValueColumnHeaderConfig;

export const getRowValues = (row: AccountsRow, headers: ValueColumnHeaderConfig[]) =>
  headers.filter((header) => !!header.value).map((header) => header.value(row));

//#endregion

export type ExportRow = { values: number[]; name: string; level: number; sum?: boolean; expanded?: boolean };

export type ExportRows = ExportRow[];

export type AccountSpecificationsQuery = IncomeAccountSpecificationsQuery &
  IncomeStatementAccountSpecificationsPortfolioQuery &
  RawBalanceAccountSpecificationsQuery &
  RawBalanceAccountSpecificationsPortfolioQuery;

export type AccountCell = {
  accountGroup: string;
  accountId: string;
  accountName: string;
  accountNumber: string;
  accountType: string;
  categoryId: string;
  period: string;
  isBlueprint: boolean;

  totalCostWithVat: number;
  change: number;
  changePercentage: number;
};

export type AccountType = "results" | "balance";

export type AccountRow = {
  id: string;
  categoryId: string;
  accountName: string;
  columnDataObjects: { period: Moment; data: ColumnDataObject }[];
  type: AccountType;
  accountNumber: string;
};

export type AccountsRow = AccountRow | CategoryRow;

export type FinancialStatementsInput = {
  id: string;
  entryDateStart: Moment;
  entryDateEnd: Moment;
};

export type StatementsModalContext = {
  account: Ref<AccountRow | undefined>;
  open: Ref<boolean>;
  period: Ref<Moment | undefined>;
};

export type BalanceTableContext = {
  budgets: Budget[];
  categoryAccountsLoading: Ref<string[]>;
  periodHeaders: PeriodColumnHeaderConfig[];
  valueHeaders: ValueColumnHeaderConfig[][];
};

export const getAccountAggregationKey = (account: { accountNumber?: string | null; accountName?: string | null }) => account.accountNumber ?? undefined;

export const BalancePreferencesInjectionKey = Symbol("balancePreferences") as InjectionKey<ComputedRef<NamespacePortfolioPropertyBalance>>;
export const BalanceTableContextInjectionKey = Symbol("balanceTableContext") as InjectionKey<ComputedRef<BalanceTableContext>>;
export const StatementsModalContextInjectionKey = Symbol("statementsModalContext") as InjectionKey<StatementsModalContext>;
export const PropertyIdsInjectionKey = Symbol("propertyIds") as InjectionKey<ComputedRef<string[]>>;
export const TenancyIdsInjectionKey = Symbol("tenancyIds") as InjectionKey<ComputedRef<string[]>>;
export const BudgetCategoryMapInjectionKey = Symbol("budgetCategoryMap") as InjectionKey<ComputedRef<PeriodBudgetCategoryAccountBudgetValuesMap>>;
export const BudgetBalanceTotalsInjectionKey = Symbol("budgetBalanceTotals") as InjectionKey<ComputedRef<Map<string, Map<string, number>>>>;

export enum BalanceAndIncomeQueryType {
  Property,
  Portfolio,
}

export type BalanceAndIncomeQueryParameters = {
  propertyId?: string | null;
  portfolioId?: string | null;
};

export const balanceDateRangeYearly = ["currentPreviousYear", "latest3", "latest5", "custom"] as const;
export const balanceDateRangeQuarterly = ["latest4", "latest8", "currentYear", "previousYear", "custom"] as const;
export const balanceDateRangeMonthly = ["currentYear", "previousYear", "currentQuarter", "previousQuarter", "latest12", "custom"] as const;
export type BalanceDateRange = (typeof balanceDateRangeYearly | typeof balanceDateRangeQuarterly | typeof balanceDateRangeMonthly)[number];

export const setToPreviousPeriod = (date: Moment, periodType: MetricPeriodType) => {
  switch (periodType) {
    case MetricPeriodType.Monthly:
      date.subtract(1, "month");
      date.startOf("month");
      break;
    case MetricPeriodType.Quarterly:
      date.subtract(1, "quarter");
      date.startOf("quarter");
      break;
    case MetricPeriodType.Yearly:
      date.subtract(1, "year");
      date.startOf("year");
      break;
  }
};

export const getBudgetDateRange = (budget: Budget) => {
  return getDateRange(
    "custom",
    MetricPeriodType.Monthly,
    moment.utc(budget.validFrom).startOf("year").format("YYYY-MM-DD"),
    moment.utc(budget.validTo).endOf("year").format("YYYY-MM-DD")
  );
};

export const getDateConfigRange = (config: DateRangeConfig) =>
  getDateRange(config.dateRange, config.periodType, config.customDateStart, config.customDateEnd, config.referenceDate);

export const getDateRange = (
  balanceDateRange: BalanceDateRange,
  periodType: MetricPeriodType,
  customDateStart?: string,
  customDateEnd?: string,
  referenceDate?: string
): { from: Moment; to: Moment } => {
  const newDate = () => moment.utc(referenceDate);
  switch (balanceDateRange) {
    case "currentPreviousYear":
      return { from: fitDate(newDate(), MetricPeriodType.Yearly).subtract(1, "year"), to: fitDate(newDate(), MetricPeriodType.Yearly) };
    case "currentYear":
      return { from: fitDate(newDate(), MetricPeriodType.Yearly), to: fitDate(newDate(), MetricPeriodType.Yearly).endOf("year") };
    case "previousYear":
      return {
        from: fitDate(newDate(), MetricPeriodType.Yearly).subtract(1, "year"),
        to: fitDate(newDate(), MetricPeriodType.Yearly).subtract(1, "year").endOf("year"),
      };
    case "latest3":
      return {
        from: fitDate(newDate(), MetricPeriodType.Yearly).subtract(3, "year"),
        to: fitDate(newDate(), MetricPeriodType.Yearly).subtract(1, "year"),
      };
    case "latest5":
      return {
        from: fitDate(newDate(), MetricPeriodType.Yearly).subtract(5, "year"),
        to: fitDate(newDate(), MetricPeriodType.Yearly).subtract(1, "year"),
      };
    case "currentQuarter":
      return { from: fitDate(newDate(), MetricPeriodType.Quarterly), to: fitDate(newDate(), MetricPeriodType.Quarterly).endOf("quarter") };
    case "previousQuarter":
      return {
        from: fitDate(newDate(), MetricPeriodType.Quarterly).subtract(1, "quarter"),
        to: fitDate(newDate(), MetricPeriodType.Quarterly).subtract(1, "quarter").endOf("quarter"),
      };
    case "latest4":
      return {
        from: fitDate(newDate(), MetricPeriodType.Quarterly).subtract(4, "quarter"),
        to: fitDate(newDate(), MetricPeriodType.Quarterly).subtract(1, "quarter"),
      };
    case "latest8":
      return {
        from: fitDate(newDate(), MetricPeriodType.Quarterly).subtract(8, "quarter"),
        to: fitDate(newDate(), MetricPeriodType.Quarterly).subtract(1, "quarter"),
      };
    case "latest12":
      return {
        from: fitDate(newDate(), MetricPeriodType.Monthly).subtract(12, "month"),
        to: fitDate(newDate(), MetricPeriodType.Monthly).subtract(1, "month"),
      };
    case "custom":
      const from = moment.utc(customDateStart, "YYYY-MM-DD").startOf("month");
      const to = moment.utc(customDateEnd, "YYYY-MM-DD").endOf("month");

      switch (periodType) {
        case MetricPeriodType.Monthly:
          from.startOf("month");
          to.endOf("month");
          break;
        case MetricPeriodType.Quarterly:
          from.startOf("quarter");
          to.endOf("quarter");
          break;
        case MetricPeriodType.Yearly:
          from.startOf("year");
          to.endOf("year");
          break;
      }

      return { from, to };
  }
};

export const getDateRangeEntries = ({ from, to }: { from: Moment; to: Moment }, periodType: MetricPeriodType) => {
  const entries: Moment[] = [];

  const current = fitDate(moment.utc(to), periodType);

  while (current.isSameOrAfter(from)) {
    entries.unshift(moment.utc(current));

    setToPreviousPeriod(current, periodType);
  }

  return entries;
};

export const extractColumnsFromCategories = (periodType: MetricPeriodType, cells: { period: string }[]) => {
  const periods = new Set<string>();

  for (let i = 0; i < cells.length; i++) {
    const cell = cells[i];

    periods.add(cell.period);
  }

  return Array.from(periods)
    .map((p) => fitDateString(p, periodType))
    .filter((a, b, c) => c.findIndex((p) => a.valueOf() === p.valueOf()) === b)
    .sort((a, b) => b.valueOf() - a.valueOf());
};

export type BalanceColumnKey = keyof NamespacePortfolioPropertyBalance["showColumns"];

export type BalanceValueHeaderConfig = {
  id: BalanceColumnKey;
  translationKey: TranslationKey;
  description?: string;
};

export const getBalanceValueHeaders = (showColumns: NamespacePortfolioPropertyBalance["showColumns"]): BalanceValueHeaderConfig[] =>
  (
    [
      { id: "totalCostWithVat", translationKey: "PORTFOLIO_BALANCE_SETTINGS_COLUMNS_VALUE" },
      { id: "change", translationKey: "PORTFOLIO_BALANCE_SETTINGS_COLUMNS_CHANGE" },
      { id: "changePercentage", translationKey: "PORTFOLIO_BALANCE_SETTINGS_COLUMNS_CHANGE_PERCENTAGE" },
    ] satisfies BalanceValueHeaderConfig[]
  ).filter((config) => showColumns[config.id]);

export const getColumnCount = (showColumns: NamespacePortfolioPropertyBalance["showColumns"]) => {
  const { change, changePercentage, totalCostWithVat } = showColumns;

  return Number(change) + Number(changePercentage) + Number(totalCostWithVat);
};

export const periodCompleted = (period: Moment, periodType: MetricPeriodType) => {
  const currentPeriod = fitDate(moment.utc(), periodType);

  return period.isBefore(currentPeriod);
};

export const getEmptyColumnDataObject = (): ColumnDataObject => ({
  budgetValues: [],
  totalCostWithVat: 0,
  change: 0,
});

export const getSetColumnDataObject = (row: AccountsRow, period: Moment) => {
  const object = getSet(
    row.columnDataObjects,
    (o) => o.period.isSame(period),
    () => ({
      period,
      data: getEmptyColumnDataObject(),
    })
  );

  return object.data;
};

export const isZeroRow = (rowValues: number[]) => rowValues.every((v) => Math.abs(v || 0) < 0.01);

export type Budget = BudgetRecordsEntity & {
  id: string;
  createdAt?: Nullable<string>;
  description?: string | null;
  validFrom?: string | null;
  validTo?: string | null;
  name?: Nullable<string>;
  propertyId?: Nullable<string>;
};

/**
 * Below 4 nested maps - periodTimestamp.budgetId.categoryId.accountId
 */
export type AccountBudgetValuesMap = Map<string, { value: number; total: number; difference: number }>;

export type CategoryAccountBudgetValuesMap = Map<string, AccountBudgetValuesMap>;

export type BudgetCategoryMapBudget = Pick<Budget, "id" | "description" | "validFrom"> & {
  categories: CategoryAccountBudgetValuesMap;
};

export type BudgetCategoryAccountBudgetValuesMap = Map<string, BudgetCategoryMapBudget>;

export type PeriodBudgetCategoryAccountBudgetValuesMap = Map<number, BudgetCategoryAccountBudgetValuesMap>;

export type ColumnDataBudgetValue = { budgetId: string; value: number; forecast: number };

export type ColumnDataObject = {
  change: number;
  totalCostWithVat: number;
  budgetValues: ColumnDataBudgetValue[];
};

export type CategoryRow = {
  id: string;
  name: string;
  columnDataObjects: { period: Moment; data: ColumnDataObject }[];
  expanded: boolean;
  level: number;
  children: CategoryRow[];
  hashes: string[];
  type: AccountType;
};

export const getSetValue = <K, V>(key: K, map: Map<K, V>, create: () => V) => {
  let value = map.get(key);

  if (!value) {
    value = create();

    map.set(key, value);
  }

  return value;
};

export const getBudgetCategoryMap = (
  budgets: Budget[],
  periodType: MetricPeriodType,
  accounts: Map<number, Map<string, Map<string, number>>> = new Map()
): PeriodBudgetCategoryAccountBudgetValuesMap => {
  const map = new Map() as PeriodBudgetCategoryAccountBudgetValuesMap;

  const getIncomeCompleted = (periodMs: number, categoryId: string, accountKey: string) => {
    if (accounts.size === 0) return 0;

    const value = accounts.get(periodMs)?.get(categoryId)?.get(accountKey);

    return value;
  };

  for (let i = 0; i < budgets.length; i++) {
    const budget = budgets[i];

    const budgetRecords = budget.assetManagementBudgetRecords?.items ?? [];

    for (let j = 0; j < budgetRecords.length; j++) {
      const record = budgetRecords[j];

      if (!record.assetManagementAccount) continue;

      const accountKey = getAccountAggregationKey(record.assetManagementAccount);

      const categoryId = record.assetManagementAccount?.categoryId;

      if (!hasValue(accountKey) || !hasValue(categoryId)) continue;

      const period = fitDateString(record.entryDate, periodType);

      const periodGroup = getSetValue(period.valueOf(), map, () => new Map<string, BudgetCategoryMapBudget>());

      const periodBudgetGroup = getSetValue(budget.id, periodGroup, () => {
        const budgetPeriodMS = fitDate(moment.utc(budget.validFrom).subtract(1, "month"), MetricPeriodType.Monthly).valueOf();

        const budgetCategories = Array.from(accounts.get(budgetPeriodMS)?.entries() ?? []).map(([categoryId, accountMap]) => {
          const categoryAccounts = Array.from(accountMap.entries() ?? []).map(([accountKey]) => [accountKey, { value: 0, total: 0, difference: 0 }] as const);

          return [categoryId, new Map<string, { value: number; total: number; difference: number }>(categoryAccounts)] as const;
        });

        const categories = new Map<string, Map<string, { value: number; total: number; difference: number }>>(budgetCategories);

        return {
          categories,
          validFrom: budget.validFrom,
          id: budget.id,
          description: budget.name ?? budget.description,
        };
      });

      const periodBudgetCategoryGroup = getSetValue(
        categoryId,
        periodBudgetGroup.categories,
        () => new Map<string, { value: number; total: number; difference: number }>()
      );

      const periodBudgetCategoryAccountsGroup = getSetValue(accountKey, periodBudgetCategoryGroup, () => ({ value: 0, total: 0, difference: 0 }));

      const actual = record.actualCostWithVat ?? 0;

      periodBudgetCategoryAccountsGroup.value += actual;

      const recordSpecificPeriod = fitDateString(record.entryDate, MetricPeriodType.Monthly);

      let completed = getIncomeCompleted(recordSpecificPeriod.valueOf(), categoryId, accountKey);

      if (hasValue(completed)) completed *= -1;

      periodBudgetCategoryAccountsGroup.difference += actual - (completed || actual);
    }

    const budgetPeriods = Array.from(map.keys()).sort((a, b) => a - b);

    const getBalanceStartingTotal = (budget: BudgetCategoryMapBudget, categoryId: string, accountKey: string) => {
      if (accounts.size === 0) return 0;

      const budgetPrePeriodMS = fitDate(moment.utc(budget.validFrom).subtract(1, "month"), MetricPeriodType.Monthly).valueOf();

      const value = accounts.get(budgetPrePeriodMS)?.get(categoryId)?.get(accountKey);

      return value;
    };

    for (let i = 0; i < budgetPeriods.length; i++) {
      const period = budgetPeriods[i];
      const prevPeriod = budgetPeriods[i - 1];

      map.get(period)?.forEach((budget, budgetId) => {
        budget.categories.forEach((accounts, categoryId) => {
          accounts.forEach((account, accountKey) => {
            const prevAccount = map.get(prevPeriod)?.get(budgetId)?.categories.get(categoryId)?.get(accountKey);

            const prevValue = prevAccount?.total ?? getBalanceStartingTotal(budget, categoryId, accountKey) ?? 0;

            account.total = prevValue + account.value;
          });
        });
      });
    }
  }

  return map;
};

export const createCategoryRow = (category: Category, children: CategoryRow[], level: number, type: AccountType): CategoryRow => ({
  id: category.id,
  name: category.name ?? "",
  level,
  hashes: [],
  columnDataObjects: [],
  expanded: level < 2 && !!children.length,
  children,
  type,
});

export const buildCategoryTree = <T extends { children: T[] }>(
  rootIds: string[],
  allCategories: Category[],
  create: (category: Category, children: T[], level: number) => T
): T[] => {
  const childrenGroups = groupBy(allCategories, { parentCategoryId: { type: "string", getKey: (item) => item.parentCategoryId } });

  const toBranch = (c: Category, level: number): T => {
    const children = (childrenGroups.get({ parentCategoryId: c.id })?.items ?? []).map((c) => toBranch(c, level + 1));

    return create(c, children, level);
  };

  const roots = rootIds.map((rootId) => allCategories.find((c) => c.id === rootId)).filter(hasValue);

  return roots.map((root) => toBranch(root, 0));
};

export const buildReactiveCategoryTree = (rootId: string, allCategories: Category[], type: AccountType) =>
  reactive(buildCategoryTree<CategoryRow>([rootId], allCategories, (c, children, level) => createCategoryRow(c, children, level, type))[0]);

const emptyBudgetValue: (budgetId: string) => ColumnDataBudgetValue = (budgetId: string) => ({ budgetId, value: 0, forecast: 0 });

/** Runs bottom up */
export const runTree = <T extends { id: string; children: T[] }>(root: T, handler: (item: T) => void, level = 0) => {
  root.children.forEach((c) => runTree(c, handler, level + 1));

  handler(root);
};

/** Runs top down. Can be stopped early by returning false */
export const runTreeDown = <T extends { id: string; children: T[] }>(root: T, handler: (item: T) => boolean, level = 0): boolean => {
  let shouldContinue = handler(root);

  if (shouldContinue) {
    for (let child of root.children) {
      shouldContinue = runTreeDown(child, handler, level + 1);

      if (!shouldContinue) return false;
    }
  }

  return shouldContinue;
};

export const sumCategoryTree = <T extends { id: string; children: T[] }>(
  {
    budgetCells = [],
    categoryTree,
    data,
    periods,
    periodType,
  }: {
    periods: Moment[];
    periodType: MetricPeriodType;
    categoryTree: T;
    data: CategoryCell[];
    budgetCells?: CategoryBudgetCell[];
  },
  handler: (
    categoryRow: T,
    period: Moment,
    data: {
      dataCells: CategoryCell[];
      budgetCells: CategoryBudgetCell[];
    }
  ) => void
) => {
  const groupedDataCells = groupBy(data, {
    period: { type: "period", getKey: (item) => fitDateString(item.period, periodType) },
    categoryId: { type: "string", getKey: (item) => item.categoryId },
  });

  const groupedBudgetCells = groupBy(budgetCells, {
    period: { type: "period", getKey: (item) => item.period },
    categoryId: { type: "string", getKey: (item) => item.categoryId },
  });

  runTree(categoryTree, (categoryRow) => {
    periods.forEach((period, index) => {
      handler(categoryRow, period, {
        dataCells:
          groupedDataCells.get({
            categoryId: categoryRow.id,
            period,
          })?.items ?? [],
        budgetCells:
          groupedBudgetCells.get({
            categoryId: categoryRow.id,
            period,
          })?.items ?? [],
      });
    });
  });
};

export const sumCategoryRowTree = ({
  budgetCells,
  categoryTree,
  data,
  periods,
  periodType,
}: {
  periods: Moment[];
  periodType: MetricPeriodType;
  categoryTree: CategoryRow;
  data: CategoryCell[];
  budgetCells?: CategoryBudgetCell[];
}) => {
  sumCategoryTree(
    {
      budgetCells,
      categoryTree,
      data,
      periods,
      periodType,
    },
    (categoryRow, period, { budgetCells: budgetCells, dataCells: dataCells }) => {
      const columnDataObject = getSetColumnDataObject(categoryRow, period);

      categoryRow.hashes.push(...dataCells.map((cell) => cell.hash).filter(Boolean));

      const childrenTotal = sum(categoryRow.children.map((child) => getSetColumnDataObject(child, period).totalCostWithVat));
      const childrenChange = sum(categoryRow.children.map((child) => getSetColumnDataObject(child, period).change));

      columnDataObject.totalCostWithVat = childrenTotal + sum(dataCells.map((cell) => cell.totalCostWithVat));
      columnDataObject.change = childrenChange + sum(dataCells.map((cell) => cell.change));

      columnDataObject.budgetValues = [];

      const childrenBudgetValues = categoryRow.children.flatMap((c) => getSetColumnDataObject(c, period).budgetValues);

      for (let childBudgetValue of childrenBudgetValues) {
        const { budgetId, value, forecast } = childBudgetValue;

        const budgetValue = getSet(
          columnDataObject.budgetValues,
          (bv) => bv.budgetId === budgetId,
          () => emptyBudgetValue(budgetId)
        );

        budgetValue.value += value;
        budgetValue.forecast += forecast;
      }

      for (let budgetCell of budgetCells) {
        const { budgetId, forecast, value } = budgetCell;

        const budgetValue = pushGet(columnDataObject.budgetValues, emptyBudgetValue(budgetId));

        budgetValue.value = value;
        budgetValue.forecast = forecast;
      }
    }
  );
};

export const sumAccounts = ({
  accountRows,
  data,
  periods,
  periodType,
  budgetCells,
}: {
  accountRows: AccountRow[];
  data: AccountCell[];
  budgetCells: AccountBudgetCell[];
  periods: Moment[];
  periodType: MetricPeriodType;
}) => {
  const groupedDataCells = groupBy(data, {
    period: { type: "period", getKey: (item) => fitDateString(item.period, periodType) },
    accountKey: { type: "string", getKey: getAccountAggregationKey },
  });

  const groupedBudgetCells = groupBy(budgetCells, {
    period: { type: "period", getKey: (item) => item.period },
    accountKey: { type: "string", getKey: (item) => item.accountKey },
  });

  for (let row of accountRows) {
    const accountKey = getAccountAggregationKey(row);

    if (!accountKey) continue;

    row.columnDataObjects = [];

    for (let period of periods) {
      const cells = groupedDataCells.get({ period, accountKey })?.items ?? [];

      const columnDataObject = getSetColumnDataObject(row, period);

      columnDataObject.totalCostWithVat = sum(cells.map((cell) => cell.totalCostWithVat));
      columnDataObject.change = sum(cells.map((cell) => cell.change));

      const budgetCellsPeriodCategory = groupedBudgetCells.get({ period, accountKey })?.items ?? [];

      for (let budgetCell of budgetCellsPeriodCategory) {
        const { budgetId, forecast, value } = budgetCell;

        const budgetValue = pushGet(columnDataObject.budgetValues, emptyBudgetValue(budgetId));

        budgetValue.value = value;
        budgetValue.forecast = forecast;
      }
    }
  }
};

export const accountsToRows = (accounts: Account[], type: AccountType) => {
  const accountRows: AccountRow[] = filterUniqueOn(accounts, getAccountAggregationKey).map((cell) => ({
    id: cell.id,
    accountName: cell.accountName ?? "",
    accountNumber: cell.accountNumber ?? "",
    categoryId: cell.categoryId ?? "",
    columnDataObjects: [],
    type,
  }));

  return accountRows.sort((a, b) => a.accountNumber.localeCompare(b.accountNumber));
};

export const accountCellsToRows = (accountCells: AccountCell[], type: AccountType) => {
  const accountRows: AccountRow[] = [];

  accountCells.forEach((cell) => {
    if (!accountRows.find((row) => getAccountAggregationKey(row) === getAccountAggregationKey(cell))) {
      accountRows.push({
        id: cell.accountId,
        accountName: cell.accountName,
        accountNumber: cell.accountNumber,
        categoryId: cell.categoryId,
        columnDataObjects: [],
        type,
      });
    }
  });

  return accountRows.sort((a, b) => a.accountNumber.localeCompare(b.accountNumber));
};

export const accountRowIsNonZero = (accountRow: AccountRow, budgets: Budget[], benchmark: BenchmarkGroupOption) =>
  accountRow.columnDataObjects.some((item) => {
    const isNonZero = (v: number) => Math.abs(v) > 0.01;

    const budgetsZero = () => {
      if (benchmark !== "budget.diff") return true;

      for (let budget of budgets) {
        const budgetValue = item.data.budgetValues.find((bv) => bv.budgetId === budget.id);

        if (budgetValue && isNonZero(budgetValue.value)) return false;
      }

      return true;
    };

    return isNonZero(item.data.totalCostWithVat) || !budgetsZero();
  });
